import { Component, ElementRef, Input, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { Router } from '@angular/router';
import { NavigationService } from '../../../vex-core/navigation/navigation.service';
import { VexLayoutService } from '@vex/services/vex-layout.service';
import { VexConfigService } from '@vex/config/vex-config.service';
import { map, startWith } from 'rxjs/operators';
import { NavigationItem } from '../../../vex-core/navigation/navigation-item.interface';
import { Observable, combineLatest, Subject } from 'rxjs';
import { SidenavUserMenuComponent } from './sidenav-user-menu/sidenav-user-menu.component';
import { SidenavItemComponent } from './sidenav-item/sidenav-item.component';
import { VexScrollbarComponent } from '@vex/components/vex-scrollbar/vex-scrollbar.component';
import { MatRippleModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SecurityService } from 'src/services/security/security.service';
import { BrandingService } from 'src/services/branding.service';
import { ApiService } from 'src/services/api.service';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { NavigationLoaderService } from 'src/app/vex/vex-core/navigation/navigation-loader.service';
import { accountRole, filterMenu } from 'src/app/vex/vex-core/navigation/navigation-menu.builder';
import { MatTooltipModule } from '@angular/material/tooltip';

/** Same intent delays as the item cards (sidenav-item). */
const USER_MENU_OPEN_DELAY_MS = 90;
const USER_MENU_CLOSE_DELAY_MS = 180;

type HeaderType = {userId: number, username: string, userphoto: string, orgId: number, orgname: string};
type ResponseType = {error: boolean, errorMessage: string, value?: string};

@Component({
  selector: 'vex-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    MatButtonModule,
    MatIconModule,
    MatRippleModule,
    VexScrollbarComponent,
    NgFor,
    SidenavItemComponent,
    AsyncPipe,
    FormsModule,
    MatTooltipModule,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    SidenavUserMenuComponent
  ]
})
export class SidenavComponent implements OnInit, OnDestroy {
  /**
   * Folded to the icon rail. The rail never spreads open on hover: an item's card (or its label as
   * a tooltip) shows beside the rail instead, and only the fold button unfolds it.
   */
  @Input() collapsed: boolean = false;

  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

  /**
   * The account menu (Dani, 02/10: like the other cards) — a hover card above the name, or beside the
   * folded rail; it also opens on click and from the keyboard. Same timings as the item cards.
   */
  userMenuOpen = false;
  @ViewChild('userProfileMenuOriginRef') private userButton?: ElementRef<HTMLElement>;
  private userOpenTimer: ReturnType<typeof setTimeout> | null = null;
  private userCloseTimer: ReturnType<typeof setTimeout> | null = null;

  /** Above the name at the menu's width; beside the 72px rail when folded. */
  get userMenuPositions(): ConnectedPosition[] {
    return this.collapsed
      ? [{ originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: 12 }]
      : [{ originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 }];
  }

  /** The card takes the name's width; on the rail (only an avatar) the unfolded menu's. */
  get userMenuWidth(): number {
    return this.collapsed ? 264 : (this.userButton?.nativeElement.clientWidth ?? 264);
  }

  items$: Observable<NavigationItem[]> = this.navigationService.items$;
  version = environment.version;

  searchQuery = '';
  private searchSubject = new Subject<string>();
  filteredItems$!: Observable<NavigationItem[]>;

  constructor(
    public securityService: SecurityService,
    public brandingService: BrandingService,
    private apiService: ApiService,
    private toastr: ToastrService,
    private navigationService: NavigationService,
    private layoutService: VexLayoutService,
    private configService: VexConfigService,
    private navLoaderService: NavigationLoaderService,
    // Optional so a test can build the component without one; only the fold button reads it.
    @Optional() private readonly router?: Router
  ) {}

  ngOnInit() {
    this.filteredItems$ = combineLatest([
      this.navLoaderService.items$,
      this.searchSubject.pipe(startWith(''))
    ]).pipe(
      map(([items, query]) => this.filterItems(items, query))
    );
  }

  onSearch(query: string): void {
    this.searchSubject.next(query);
  }

  /**
   * Label, synonyms and card sub-pages all count (e.g. "invoice" finds Finances › Invoices and
   * Settings › Plans & billing); matched sub-pages are listed under their item.
   */
  private filterItems(items: NavigationItem[], query: string): NavigationItem[] {
    return filterMenu(items, query);
  }

  /** "Admin" or "Member", shown after the organization on the account button. */
  get roleLabel(): string {
    return accountRole(this.securityService.securityObject);
  }

  /** The rail's search icon: unfold the menu and put the cursor in the search box. */
  openSearch(): void {
    if (this.collapsed) this.toggleCollapse();
    // The box is rendered once the unfolded state has reached this component.
    setTimeout(() => this.searchInput?.nativeElement.focus());
  }

  /**
   * Folds or unfolds the sidenav, remembering the choice for next time.
   *
   * Routed through the config rather than straight to the layout service: the config is
   * already saved to localStorage and re-applied on load, so writing the state here is
   * what makes the choice survive a reload. `_setSidenavState` calls the layout service
   * for us, so collapsing here as well would be redundant.
   *
   * Automatic collapses — the review screens fold the sidenav on entry and restore it on
   * exit — deliberately keep calling the layout service directly, so a temporary fold the
   * user did not ask for is never saved as their preference.
   */
  toggleCollapse() {
    this.closeUserMenu();
    // The rail has no search box, so a filter left behind would hide items with no visible reason.
    if (!this.collapsed && this.searchQuery) {
      this.searchQuery = '';
      this.onSearch('');
    }
    // A review screen folds the menu by itself and folds it again whenever the saved config changes
    // (layout.component `syncSidenavToRoute`), so a saved choice would be undone at once and the menu
    // could never be opened there. On those screens the button folds / unfolds for this visit only,
    // through the layout service like the automatic fold, and nothing is saved as the preference.
    if (this.onReviewScreen()) {
      if (this.collapsed) this.layoutService.expandSidenav();
      else this.layoutService.collapseSidenav();
      return;
    }
    this.configService.updateConfig({
      sidenav: { state: this.collapsed ? 'expanded' : 'collapsed' }
    });
  }

  /** The same test the layout uses for the screens it folds automatically. */
  private onReviewScreen(): boolean {
    return !!this.router?.url.split('?')[0].startsWith('/review/');
  }

  trackByRoute(index: number, item: NavigationItem): string {
    if (item.type === 'link' && typeof item.route === 'string') {
      return item.route + JSON.stringify(item.queryParams ?? {});
    }

    return item.type + ':' + item.label;
  }

  /** Pointer on the name: open after the cards' short intent delay. */
  onUserEnter(): void {
    this.cancelUserClose();
    if (this.userMenuOpen || this.userOpenTimer) return;
    this.userOpenTimer = setTimeout(() => { this.userOpenTimer = null; this.userMenuOpen = true; }, USER_MENU_OPEN_DELAY_MS);
  }

  /** Pointer left the name or the card: close unless it comes back in time. */
  onUserLeave(): void {
    if (this.userOpenTimer) { clearTimeout(this.userOpenTimer); this.userOpenTimer = null; }
    if (!this.userMenuOpen) return;
    this.cancelUserClose();
    this.userCloseTimer = setTimeout(() => this.closeUserMenu(), USER_MENU_CLOSE_DELAY_MS);
  }

  /** Pointer is on the card: keep it open while it is used. */
  onUserMenuEnter(): void {
    this.cancelUserClose();
  }

  /** A click on the name opens the card at once (a hover may already have). */
  openUserMenu(): void {
    this.cancelUserClose();
    if (this.userOpenTimer) { clearTimeout(this.userOpenTimer); this.userOpenTimer = null; }
    this.userMenuOpen = true;
  }

  /** Enter / Space on the name opens the card, as a click does. */
  onProfileKey(event: Event): void {
    event.preventDefault();
    this.openUserMenu();
  }

  /** Escape closes the card and hands focus back to the name. */
  onUserMenuKey(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeUserMenu();
    this.userButton?.nativeElement.focus();
  }

  /** A click outside closes it — except on the name itself, which opens it. */
  onUserMenuOutsideClick(event: MouseEvent): void {
    if (this.userButton?.nativeElement.contains(event.target as Node)) return;
    this.closeUserMenu();
  }

  closeUserMenu(): void {
    this.cancelUserClose();
    this.userMenuOpen = false;
  }

  private cancelUserClose(): void {
    if (this.userCloseTimer) { clearTimeout(this.userCloseTimer); this.userCloseTimer = null; }
  }

  ngOnDestroy(): void {
    if (this.userOpenTimer) clearTimeout(this.userOpenTimer);
    this.cancelUserClose();
  }
}
